import type { User } from '@auth0/auth0-vue'
import { createAuth0 } from '@auth0/auth0-vue'

// Admin's own Auth0 SPA integration — talks to @auth0/auth0-vue directly
// rather than through @package/nuxt-auth0 (that module's bundled pages are
// styled for the UnoCSS-based apps; admin uses Nuxt UI/Tailwind, and its
// login/callback/status pages live under app/pages/auth/**).
export default defineNuxtPlugin({
  name: '001-auth0',
  parallel: true,
  setup: (nuxtApp) => {
    // Skip on server-side rendering; admin is ssr:false so this only ever
    // matters for the initial client hydration pass.
    if (import.meta.server) return

    const config = nuxtApp.$config.public

    const plugin = createAuth0(
      {
        domain: config.AUTH0_DOMAIN,
        clientId: config.AUTH0_CLIENT_ID,
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: `${location.origin}/auth/callback`,
          audience: config.AUTH0_AUDIENCE,
        },
      },
      {
        errorPath: '/auth/status',
        skipRedirectCallback: location.pathname === '/auth/callback',
      },
    )
    nuxtApp.vueApp.use(plugin)

    const auth0 = () => plugin

    return {
      provide: {
        auth0,
        auth0Ready: async (
          options: { timeout?: number } = { timeout: 2000 },
        ): Promise<User | undefined> => {
          const { isLoading, user } = auth0()
          if (user.value?.sub) return user.value

          const { promise: loaded, resolve: resolveLoaded } =
            Promise.withResolvers<true>()
          const interval = setInterval(() => {
            if (!isLoading.value) {
              clearInterval(interval)
              resolveLoaded(true)
            }
          }, 100)

          const { promise: timedOut, resolve: resolveTimedOut } =
            Promise.withResolvers<true>()
          setTimeout(() => resolveTimedOut(true), options.timeout)

          await Promise.any([loaded, timedOut])

          return user.value
        },
        loginWithRedirect: (email = '') => {
          auth0().loginWithRedirect({
            authorizationParams: { login_hint: email },
          })
        },
      },
    }
  },
})
